import {useEffect, useLayoutEffect, useRef, useState} from 'react'
import {Link, useLocation} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import classNames from 'classnames'

import {useActions} from 'hooks'
import {checkPermission, usePermissions} from 'rbac'
import {SETTINGS_NAV_PAGES} from 'components/Navigation/SettingsNavigation/settingsNavPages'
import {
  settingsMobileStore,
  useSettingsMobileState,
} from './settingsMobileStore'
import {coveredStyle} from './SettingsMobilePage'
import {SettingsMobileChevron} from './SettingsMobileList'
import './Settings.mobile.css'

const FLASH_MS = 700

/** Phone home for Settings: the left navigation as a drill-in list. */
export function SettingsMobileHome(): JSX.Element {
  const {t} = useTranslation(['settings'])
  const {pathname} = useLocation()
  const {doIHaveAccess} = useActions()
  const {permissions} = usePermissions()
  const {isPageOpen, dragOffset} = useSettingsMobileState()
  const [flashPath, setFlashPath] = useState('')
  const wasPageOpen = useRef(isPageOpen)
  const list = useRef<HTMLElement>(null)

  const pages = SETTINGS_NAV_PAGES.filter(
    page =>
      checkPermission([page.permission], permissions) &&
      (!page.adminOnly || doIHaveAccess),
  )

  // Returning from a page flashes its row and hands focus back to it.
  useLayoutEffect(() => {
    if (wasPageOpen.current && !isPageOpen) {
      setFlashPath(pathname)
      const focused = document.activeElement
      if (
        !focused ||
        focused === document.body ||
        !focused.isConnected ||
        focused.closest('.settings-mobile-pane')
      ) {
        list.current
          ?.querySelector<HTMLElement>(`[data-settings-path="${pathname}"]`)
          ?.focus({preventScroll: true})
      }
    }
    wasPageOpen.current = isPageOpen
  }, [isPageOpen, pathname])

  useEffect(() => {
    if (!flashPath) return
    const timer = setTimeout(() => setFlashPath(''), FLASH_MS)
    return () => clearTimeout(timer)
  }, [flashPath])

  return (
    <nav
      ref={list}
      className="settings-mobile-home h-full overflow-y-auto overscroll-contain"
      aria-label={t('SETTINGS_TITLE')}
      aria-hidden={isPageOpen || undefined}
      {...(isPageOpen ? {inert: ''} : {})}
      data-dragging={dragOffset > 0}
      style={isPageOpen ? coveredStyle(dragOffset) : undefined}
    >
      <h1 className="px-5 pt-4 pb-2.5 abc-light text-type-h1 text-white">
        {t('SETTINGS_TITLE')}
      </h1>
      <ul className="px-1">
        {pages.map(page => (
          <li key={page.pathname}>
            <Link
              to={page.pathname}
              data-cy={`mobile-${page.pathname.replaceAll('/', '')}`}
              data-settings-path={page.pathname}
              data-flash={flashPath === page.pathname || undefined}
              onClick={event => {
                // Reopening the current page only slides it back in; routing
                // to the same path would trip its unsaved-changes prompt.
                if (page.pathname === pathname) event.preventDefault()
                settingsMobileStore.setPageOpen(true)
              }}
              className={classNames(
                'settings-mobile-home-row flex items-center h-[3.75rem] pl-4 pr-3 rounded-ds-md',
                'abc-light text-type-h2 text-white touch-manipulation',
              )}
            >
              <span className="flex-1 min-w-0 truncate">
                {t(page.labelKey)}
              </span>
              <SettingsMobileChevron />
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  )
}
